import { IVisionWorkflowData } from "@kuika/kuika-cl-model";
import * as React from "react";
import KuikaBadge from "../../../ui-kit/kuika-badge/kuika-badge";
import KuikaButton from "../../../ui-kit/kuika-button/kuika-button";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import "../ai/ai-action-template-detail-modal.scss";

// ─── Inner Components ─────────────────────────────────────────────────────────

interface AccordionItemProps {
  title: string | React.ReactNode;
  isOpen: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}

const AccordionItem: React.FC<AccordionItemProps> = ({ title, isOpen, onToggle, children }) => (
  <div className={`ai-template-detail__accordion-item ${isOpen ? "ai-template-detail__accordion-item--active" : ""}`}>
    <div className="ai-template-detail__accordion-header" onClick={onToggle}>
      <KuikaSvg
        type="arrows"
        name="chevron-down"
        width={14}
        height={14}
        style={{
          transform: isOpen ? "rotate(0deg)" : "rotate(-90deg)",
          transition: "transform 0.2s ease"
        }}
      />
      <span className="ai-template-detail__accordion-label">{title}</span>
    </div>
    <div
      className={`ai-template-detail__accordion-content ${isOpen ? "ai-template-detail__accordion-content--open" : ""}`}
    >
      <div className="ai-template-detail__accordion-content-inner">{children}</div>
    </div>
  </div>
);

// ─── Helpers ──────────────────────────────────────────────────────────────────

interface INodeCategory {
  count: number;
  label: string;
}

/** Categorize workflow nodes into functional groups (mirrors renderVisionActionDetails logic). */
const categorizeNodes = (
  nodes: IVisionWorkflowData["nodes"]
): { categories: INodeCategory[]; requiresGpu: boolean } => {
  const buckets: Record<string, INodeCategory> = {
    input: { count: 0, label: "Input Sources" },
    processing: { count: 0, label: "AI Processing" },
    analytics: { count: 0, label: "Analytics" },
    services: { count: 0, label: "Services" },
    other: { count: 0, label: "Other" }
  };
  let requiresGpu = false;

  nodes.forEach((node) => {
    const t = (node.type || "").toLowerCase();
    if (t === "videoinput" || t.includes("source") || t.includes("input") || t.includes("rtsp")) {
      buckets.input.count++;
    } else if (
      t === "detector" ||
      t === "tracker" ||
      t === "segmentation" ||
      t.includes("detect") ||
      t.includes("inference")
    ) {
      buckets.processing.count++;
      requiresGpu = true;
    } else if (t.includes("analytics") || t.includes("metrics")) {
      buckets.analytics.count++;
    } else if (t.includes("database") || t.includes("api") || t.includes("service")) {
      buckets.services.count++;
    } else {
      buckets.other.count++;
    }

    if ((node as any).data?.config?.requiresGpu) {
      requiresGpu = true;
    }
  });

  const categories = Object.values(buckets).filter((c) => c.count > 0);
  return { categories, requiresGpu };
};

const getCategoryBadgeColor = (label: string): "blue" | "yellow" | "green" | "purple" | "orange" | "pink" => {
  if (label.includes("Input")) return "blue";
  if (label.includes("Processing")) return "green";
  if (label.includes("Analytics")) return "yellow";
  if (label.includes("Services")) return "purple";
  return "blue";
};

// ─── Props & State ────────────────────────────────────────────────────────────

interface IVisionTemplateDetailModalProps {
  visible: boolean;
  template: {
    id: string;
    name: string;
    description: string;
    imageUrl?: string;
    type?: string;
    workflowData: IVisionWorkflowData;
  } | null;
  onOk: () => void;
  onCancel: () => void;
}

interface IVisionTemplateDetailModalState {
  isDescriptionExpanded: boolean;
  activeAccordionKeys: string[];
}

// ─── Component ────────────────────────────────────────────────────────────────

class VisionTemplateDetailModal extends React.PureComponent<
  IVisionTemplateDetailModalProps,
  IVisionTemplateDetailModalState
> {
  constructor(props: IVisionTemplateDetailModalProps) {
    super(props);
    this.state = {
      isDescriptionExpanded: false,
      activeAccordionKeys: ["nodes", "infra"]
    };
  }

  componentDidUpdate(prevProps: IVisionTemplateDetailModalProps) {
    if (this.props.visible && !prevProps.visible) {
      this.setState({
        isDescriptionExpanded: false,
        activeAccordionKeys: ["nodes", "infra"]
      });
    }
  }

  private toggleAccordion = (key: string) => {
    this.setState((prev) => ({
      activeAccordionKeys: prev.activeAccordionKeys.includes(key)
        ? prev.activeAccordionKeys.filter((k) => k !== key)
        : [...prev.activeAccordionKeys, key]
    }));
  };

  /** Renders the Nodes accordion body – mirrors renderVisionActionDetails categories. */
  renderNodesContent = () => {
    const { template } = this.props;
    const nodes = template?.workflowData?.nodes;
    if (!nodes || nodes.length === 0) {
      return (
        <div className="ai-template-detail__empty-container">
          <span className="ai-template-detail__empty">No nodes configured</span>
        </div>
      );
    }

    const { categories } = categorizeNodes(nodes);
    const edgeCount = template?.workflowData?.edges?.length || 0;

    return (
      <div className="ai-template-detail__params-list">
        {categories.map((cat) => (
          <div key={cat.label} className="ai-template-detail__param-item">
            <span className="ai-template-detail__param-name ai-template-detail__param-name--indented">{cat.label}</span>
            <KuikaBadge content={String(cat.count)} color={getCategoryBadgeColor(cat.label)} />
          </div>
        ))}
        {/* <div className="ai-template-detail__param-item">
          <span className="ai-template-detail__param-name">Connections</span>
          <KuikaBadge content={String(edgeCount)} color="blue" />
        </div> */}
      </div>
    );
  };

  /** Renders the Infrastructure accordion body – GPU requirement & estimated cost. */
  renderInfraContent = () => {
    const { template } = this.props;
    const nodes = template?.workflowData?.nodes;
    if (!nodes || nodes.length === 0) {
      return (
        <div className="ai-template-detail__empty-container">
          <span className="ai-template-detail__empty">No infrastructure details available</span>
        </div>
      );
    }

    const { requiresGpu } = categorizeNodes(nodes);

    // Estimated cost – same placeholder logic as renderVisionActionDetails
    const baseCost = 0.05;
    const gpuCost = requiresGpu ? 0.5 : 0;
    const nodeCost = nodes.length * 0.02;
    const estimatedCost = (baseCost + gpuCost + nodeCost).toFixed(2);

    return (
      <div className="ai-template-detail__params-list">
        <div className="ai-template-detail__param-item">
          <span className="ai-template-detail__param-name">GPU Required</span>
          <KuikaBadge content={requiresGpu ? "Yes" : "No"} color={requiresGpu ? "green" : "blue"} />
        </div>
        <div className="ai-template-detail__param-item">
          <span className="ai-template-detail__param-name">Est. Cost</span>
          <KuikaBadge content={`$${estimatedCost}/hr`} color="orange" />
        </div>
        <div className="ai-template-detail__param-item">
          <span className="ai-template-detail__param-name">Total Nodes</span>
          <KuikaBadge content={String(nodes.length)} color="green" />
        </div>
      </div>
    );
  };

  render() {
    const { visible, template, onOk, onCancel } = this.props;

    if (!template || !visible) return null;

    return (
      <div className="ai-template-detail-overlay" onClick={onCancel}>
        <div className="ai-template-detail-panel" onClick={(e) => e.stopPropagation()}>
          {/* Panel Header */}
          <div className="ai-template-detail-panel__header">
            <div className="ai-template-detail-panel__header-title">Continue with this template</div>
            <div className="ai-template-detail-panel__header-left">
              <KuikaButton
                type="tertiary"
                color="neutral"
                size="sm"
                isIcon
                aria-label="Close template details"
                onClick={onCancel}
              >
                <KuikaSvg type="common" name="x" width={14} height={14} color="var(--dark-7)" />
              </KuikaButton>
            </div>
          </div>

          {/* Scrollable Content */}
          <div className="ai-template-detail-panel__body">
            {/* Template image + description */}
            <div className="ai-template-detail__header">
              <img
                src={template.imageUrl || "/images/ai-template-thumb.png"}
                alt={template.name}
                className="ai-template-detail__image"
              />
              <div className="ai-template-detail__header-info">
                <div className="ai-template-detail-panel__header-title">
                  <div className="ai-template-detail-panel__title">{template.name}</div>
                  {template.type && <KuikaBadge content={template.type} color="green" />}
                </div>
                <div className="ai-template-detail__description-wrapper">
                  <p
                    className={`ai-template-detail__description ${
                      !this.state.isDescriptionExpanded ? "ai-template-detail__description--truncated" : ""
                    }`}
                  >
                    {template.description}
                  </p>
                  {template.description && template.description.length > 150 && (
                    <span
                      className="ai-template-detail__description-toggle"
                      onClick={() =>
                        this.setState({
                          isDescriptionExpanded: !this.state.isDescriptionExpanded
                        })
                      }
                    >
                      {this.state.isDescriptionExpanded ? "Less" : "More..."}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Accordion content */}
            <div className="ai-template-detail__content">
              <div className="ai-template-detail__accordion">
                <AccordionItem
                  title="Nodes"
                  isOpen={this.state.activeAccordionKeys.includes("nodes")}
                  onToggle={() => this.toggleAccordion("nodes")}
                >
                  {this.renderNodesContent()}
                </AccordionItem>
                <AccordionItem
                  title="Infrastructure"
                  isOpen={this.state.activeAccordionKeys.includes("infra")}
                  onToggle={() => this.toggleAccordion("infra")}
                >
                  {this.renderInfraContent()}
                </AccordionItem>
              </div>
            </div>
          </div>

          {/* Sticky Footer */}
          <div className="ai-template-detail-panel__footer">
            <KuikaButton type="primary" size="md" uppercase onClick={onOk}>
              Start with this template
            </KuikaButton>
          </div>
        </div>
      </div>
    );
  }
}

export default VisionTemplateDetailModal;
